import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../constants';
import './CancellationPage.css';

interface CancellationAppointment {
  id: string;
  when: string;
  procedureName?: string;
  practitionerIds?: string[];
  previousPage?: string;
}

interface FullPractitioner {
  //id: string; // DEPRECATED, use userId
  userId: string;
  firstName?: string;
  familyName?: string;
  title?: string;
}

// Pending cancellation data is not proof of a successful backend response.
const storageKey = 'lastCancelledAppointment';

const readStoredAppointment = (): CancellationAppointment | null => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as CancellationAppointment) : null;
  } catch {
    return null;
  }
};

const formatNameInitials = (firstName?: string) =>
  String(firstName ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0)}.`)
    .join(' ');

const formatPractitionerName = (practitioner?: FullPractitioner) => {
  if (!practitioner) {
    return 'Nicht angegeben';
  }

  const title = String(practitioner.title ?? '').trim();
  const initials = formatNameInitials(practitioner.firstName);
  const familyName = String(practitioner.familyName ?? '').trim();

  return [title, initials, familyName].filter(Boolean).join(' ').trim() || 'Nicht angegeben';
};

const formatProcedureName = (value?: string) => value?.trim() || 'Behandlung';

const formatDate = (when?: string) => {
  if (!when) {
    return '--';
  }

  const date = new Date(when);
  if (Number.isNaN(date.getTime())) {
    return when;
  }

  return date.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatTimeRange = (when?: string) => {
  if (!when) {
    return '--';
  }

  const start = new Date(when);
  if (Number.isNaN(start.getTime())) {
    return when;
  }

  const end = new Date(start);
  end.setMinutes(end.getMinutes() + 45);

  const startText = start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const endText = end.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return `${startText} - ${endText} Uhr`;
};

const detailLabels = ['Behandlung', 'Behandler', 'Datum', 'Zeit'] as const;

export function CancellationDonePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const appointmentFromState = (location.state as { appointment?: CancellationAppointment } | null)?.appointment;
  const appointment = useMemo(() => appointmentFromState ?? readStoredAppointment(), [appointmentFromState]);
  const [practitionerName, setPractitionerName] = useState('Nicht angegeben');

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  useEffect(() => {
    if (!appointment?.id || !appointment.when) {
      return;
    }

    localStorage.setItem(storageKey, JSON.stringify(appointment));
  }, [appointment]);

  useEffect(() => {
    const practitionerId = appointment?.practitionerIds?.[0];
    const token = localStorage.getItem('token');

    if (!practitionerId || !token) {
      setPractitionerName('Nicht angegeben');
      return;
    }

    let isActive = true;

    const loadPractitioner = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/practitioners/full/${practitionerId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load practitioner: ${response.status}`);
        }

        const practitioner = (await response.json()) as FullPractitioner;
        if (isActive) {
          setPractitionerName(formatPractitionerName(practitioner));
        }
      } catch (error) {
        console.error('Failed to load practitioner details', error);
        if (isActive) {
          setPractitionerName('Nicht angegeben');
        }
      }
    };

    loadPractitioner();

    return () => {
      isActive = false;
    };
  }, [appointment]);

  if (!appointment?.id || !appointment.when) {
    return (
      <div className="cancellation-page">
        <div className="cancellation-page__card cancellation-page__empty">
          <h1 className="text-3xl font-semibold text-slate-900">Kein Termin gefunden</h1>
          <p className="mt-4 text-sm leading-6 text-slate-400">
            Die Daten für die Stornierungsbestätigung sind aktuell nicht verfügbar.
          </p>
        </div>
      </div>
    );
  }

  const detailValues = [
    formatProcedureName(appointment.procedureName),
    practitionerName,
    formatDate(appointment.when),
    formatTimeRange(appointment.when),
  ];

  return (
    <div className="cancellation-page cancellation-page--done">
      <main className="cancellation-page__card" aria-labelledby="cancellation-done-title">
        <div className="cancellation-page__heading">
          <div className="cancellation-page__icon cancellation-page__icon--success">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
              <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
              <path d="m8.5 12 2.3 2.3 4.7-4.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          <h1 id="cancellation-done-title">
            Termin erfolgreich abgesagt
          </h1>
          <p className="cancellation-page__description">
            Ihr Termin wurde erfolgreich abgesagt. Sie erhalten in Kürze eine Bestätigungs-E-Mail.
          </p>
        </div>

        <section className="cancellation-page__details" aria-labelledby="cancelled-appointment-details-title">
          <h2 id="cancelled-appointment-details-title">Abgesagter Termin</h2>

          <dl>
            {detailLabels.map((label, index) => (
              <div
                key={label}
                className="cancellation-page__detail"
              >
                <dt>{label}</dt>
                <dd>{detailValues[index]}</dd>
              </div>
            ))}
          </dl>
        </section>

        <div className="cancellation-page__actions">
          <button
            type="button"
            onClick={() => navigate('/procedures')}
            className="cancellation-page__cancel"
          >
            Neuen Termin vereinbaren
          </button>
          <button
            type="button"
            onClick={() => navigate('/appointments')}
            className="cancellation-page__keep"
          >
            Zu meinen Terminen
          </button>
        </div>

        <p className="cancellation-page__contact">
          Haben Sie Fragen? Kontaktieren Sie uns unter{' '}
          <span>052 624 46 25</span>.
        </p>
      </main>
    </div>
  );
}
